import { useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Eye,
  Mail,
  MessageCircle,
  MessageCircleMore,
  MessageSquare,
  MousePointerClick,
  Plus,
  Search,
  Send,
} from "lucide-react";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { CampaignBuilderModal } from "@/components/admin/marketing/campaigns/campaign-builder-modal";
import { CampaignDetailDrawer } from "@/components/admin/marketing/campaigns/campaign-detail-drawer";
import {
  CHANNEL_OPTIONS,
  STATUS_COLORS,
  STATUS_OPTIONS,
  useCampaignLabels,
  type Campaign,
  type CampaignListResponse,
} from "@/components/admin/marketing/campaigns/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Marketing → Campaigns.
 *
 * Port of `src/app/admin/marketing/campaigns/page.tsx` (legacy NextJS, ~407 LOC).
 *
 * V1.1 P12 scope:
 *   - List with country pill filter, status/channel/search filters, pagination
 *   - KPI cards (total, sent, open rate, click rate)
 *   - Campaign builder modal (5 tabs: Basics → Audience → Content → Schedule → Review)
 *   - Campaign detail drawer (stats, content snapshot, quick actions)
 *
 * D21 declared exceptions vs legacy:
 *   - `useAdminFetch` → `apiFetch` (Architecture Discipline Rule 1)
 *   - 5-tab builder replaces single-screen Dialog (legacy was cramped)
 *   - Status/channel filter + search added (legacy filtered country only)
 *   - Detail drawer added — no legacy equivalent
 *
 * Endpoints (verified in apps/api/src/routes/admin-marketing.ts):
 *   GET   /api/admin/marketing/campaigns?status=&country=&limit=&offset=
 *   POST  /api/admin/marketing/campaigns
 *   GET   /api/admin/marketing/campaigns/:id
 *   PATCH /api/admin/marketing/campaigns/:id
 *   POST  /api/admin/marketing/campaigns/preview-segment
 *   GET   /api/admin/marketing/templates       (501-tolerant)
 *   POST  /api/admin/marketing/templates       (501-tolerant)
 *   POST  /api/admin/marketing/ai-generate     (501-tolerant)
 *
 * The list is a query keyed on the filters and `dbTarget` (Local↔Prod admin
 * toggle). The builder is re-keyed on every open, so it always starts from the
 * campaign it was opened for.
 */

export const Route = createFileRoute("/_authenticated/marketing-campaigns")({
  component: MarketingCampaignsPage,
});

const CHANNEL_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  sms: MessageSquare,
  viber: MessageCircle,
  whatsapp: MessageCircleMore,
};

const CAMPAIGNS_QUERY_PREFIX = ["admin", "marketing", "campaigns"] as const;

const PAGE_SIZE = 25;

const FIELD_CLASS = "border-border bg-background min-h-11 rounded-lg border px-2.5 text-sm";

function MarketingCampaignsPage() {
  const { t } = useLingui();
  const { statusLabel } = useCampaignLabels();
  const queryClient = useQueryClient();
  const { visibleCountries: countries } = useAdminCountries();
  const { country: headerCountry, setCountry: setHeaderCountry, dbTarget } = useAdminContext();
  const selectedCountry = headerCountry ?? "";
  const setSelectedCountry = (code: string) => setHeaderCountry(code === "" ? null : code);

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of countries) {
      if (c.totalProviders > 0) map[c.code] = c.totalProviders;
    }
    return map;
  }, [countries]);

  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Modal/drawer state
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderKey, setBuilderKey] = useState(0);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [openCampaignId, setOpenCampaignId] = useState<string | null>(null);

  const limit = PAGE_SIZE;

  const campaignsQuery = useQuery({
    queryKey: [...CAMPAIGNS_QUERY_PREFIX, selectedCountry, statusFilter, offset, dbTarget],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.set("country", selectedCountry);
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      return apiFetch.get<CampaignListResponse>(`/api/admin/marketing/campaigns?${params}`);
    },
    placeholderData: keepPreviousData,
  });

  const campaigns = useMemo(
    () => (campaignsQuery.isError ? [] : (campaignsQuery.data?.campaigns ?? [])),
    [campaignsQuery.isError, campaignsQuery.data],
  );
  const total = campaignsQuery.isError ? 0 : (campaignsQuery.data?.total ?? 0);
  // A filter or page change shows the skeleton again, as a first load does.
  const loading =
    campaignsQuery.isPending || (campaignsQuery.isPlaceholderData && campaignsQuery.isFetching);
  const error = campaignsQuery.isError
    ? campaignsQuery.error instanceof ApiError
      ? `HTTP ${campaignsQuery.error.status}`
      : (campaignsQuery.error as Error).message
    : null;

  const refresh = () => void queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_PREFIX });

  /** Builder for a new campaign (null) or an existing one — always a fresh mount. */
  const openBuilder = (campaign: Campaign | null) => {
    setEditing(campaign);
    setBuilderKey((k) => k + 1);
    setShowBuilder(true);
  };

  // Client-side search + channel filter (server only filters status/country).
  const filteredCampaigns = useMemo(() => {
    let list = campaigns;
    if (channelFilter) list = list.filter((c) => c.channel === channelFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) => c.name.toLowerCase().includes(q) || (c.description ?? "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [campaigns, channelFilter, search]);

  // KPIs derived from current page
  const totalSent = filteredCampaigns.reduce((s, c) => s + (c.totalSent ?? 0), 0);
  const totalOpened = filteredCampaigns.reduce((s, c) => s + (c.totalOpened ?? 0), 0);
  const totalClicked = filteredCampaigns.reduce((s, c) => s + (c.totalClicked ?? 0), 0);
  const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0;
  const clickRate = totalSent > 0 ? Math.round((totalClicked / totalSent) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            <Trans>Campaigns</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>Multi-channel marketing — email, SMS, Viber, WhatsApp · {total} total</Trans>
          </p>
        </div>
        <Button className="min-h-11" onClick={() => openBuilder(null)}>
          <Plus className="size-4" />
          <Trans>New campaign</Trans>
        </Button>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={(code) => {
          setSelectedCountry(code);
          setOffset(0);
        }}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: t`Total campaigns`, value: total, icon: Send },
          { label: t`Messages sent`, value: totalSent, icon: Mail },
          { label: t`Open rate`, value: `${openRate}%`, icon: Eye },
          { label: t`Click rate`, value: `${clickRate}%`, icon: MousePointerClick },
        ].map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label}>
              <CardContent className="flex items-center gap-3 p-3">
                <div className="bg-primary-soft text-primary flex size-9 items-center justify-center rounded-lg">
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-lg font-bold tabular-nums">{kpi.value}</div>
                  <div className="text-muted-foreground truncate text-[0.625rem] uppercase">
                    {kpi.label}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-3">
          <div className="min-w-[200px] flex-1">
            <label htmlFor="campaigns-search" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Search</Trans>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <input
                  id="campaigns-search"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
                  placeholder={t`Name or description…`}
                  className={`${FIELD_CLASS} w-full pr-3 pl-8 outline-none`}
                />
              </div>
              <Button className="min-h-11" onClick={() => setSearch(searchInput)}>
                <Trans>Search</Trans>
              </Button>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label htmlFor="campaigns-status" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Status</Trans>
            </label>
            <select
              id="campaigns-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setOffset(0);
              }}
              className={`${FIELD_CLASS} w-full`}
            >
              <option value="">{t`All statuses`}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label htmlFor="campaigns-channel" className="text-muted-foreground mb-1 block text-xs">
              <Trans>Channel</Trans>
            </label>
            <select
              id="campaigns-channel"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className={`${FIELD_CLASS} w-full`}
            >
              <option value="">{t`All channels`}</option>
              {CHANNEL_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="border-border/50 overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border/50 bg-muted/30 border-b">
                <th className="px-3 py-2 text-left font-semibold">
                  <Trans>Campaign</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold sm:table-cell">
                  <Trans>Channel</Trans>
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  <Trans>Status</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
                  <Trans>Audience</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                  <Trans>Sent</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                  <Trans>Open</Trans>
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                  <Trans>Click</Trans>
                </th>
                <th className="hidden px-3 py-2 text-left font-semibold xl:table-cell">
                  <Trans>Scheduled</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-border/30 border-b last:border-0">
                    <td colSpan={8} className="px-3 py-2.5">
                      <Skeleton className="h-8" />
                    </td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={8} className="text-destructive p-8 text-center text-sm">
                    {error}
                  </td>
                </tr>
              ) : filteredCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted-foreground p-8 text-center">
                    <div className="mx-auto max-w-sm">
                      <Mail className="mx-auto mb-2 size-8 opacity-40" />
                      <p className="text-sm">
                        <Trans>No campaigns match the current filter.</Trans>
                      </p>
                      <Button
                        variant="outline"
                        className="mt-3 min-h-11"
                        onClick={() => openBuilder(null)}
                      >
                        <Plus className="size-3.5" />
                        <Trans>Create first campaign</Trans>
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCampaigns.map((c) => {
                  const ChannelIcon = CHANNEL_ICONS[c.channel] ?? Mail;
                  const sent = c.totalSent ?? 0;
                  const oRate = sent > 0 ? Math.round(((c.totalOpened ?? 0) / sent) * 100) : 0;
                  const cRate = sent > 0 ? Math.round(((c.totalClicked ?? 0) / sent) * 100) : 0;
                  const campaignName = c.name;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setOpenCampaignId(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setOpenCampaignId(c.id);
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={t`Open campaign ${campaignName}`}
                      className="border-border/30 hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:outline-primary cursor-pointer border-b last:border-0 focus-visible:outline-2 focus-visible:-outline-offset-2"
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{c.name}</span>
                          {c.country && (
                            <Badge variant="secondary" className="text-[0.625rem] uppercase">
                              {c.country}
                            </Badge>
                          )}
                        </div>
                        {c.description && (
                          <div className="text-muted-foreground line-clamp-1 text-xs">
                            {c.description}
                          </div>
                        )}
                      </td>
                      <td className="hidden px-3 py-2.5 sm:table-cell">
                        <Badge variant="secondary" className="text-[0.625rem]">
                          <ChannelIcon className="mr-1 size-3" />
                          {c.channel}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge
                          variant="outline"
                          className={`text-[0.625rem] ${STATUS_COLORS[c.status] ?? ""}`}
                        >
                          {statusLabel(c.status)}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground hidden px-3 py-2.5 text-right text-xs tabular-nums md:table-cell">
                        {c.recipientCount}
                      </td>
                      <td className="hidden px-3 py-2.5 text-right text-xs tabular-nums lg:table-cell">
                        {sent}
                      </td>
                      <td className="text-muted-foreground hidden px-3 py-2.5 text-right text-xs tabular-nums lg:table-cell">
                        {oRate}%
                      </td>
                      <td className="text-muted-foreground hidden px-3 py-2.5 text-right text-xs tabular-nums lg:table-cell">
                        {cRate}%
                      </td>
                      <td className="text-muted-foreground hidden px-3 py-2.5 text-xs xl:table-cell">
                        {c.scheduledAt ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarClock className="size-3" />
                            {new Date(c.scheduledAt).toLocaleDateString()}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            <Trans>
              {offset + 1}–{Math.min(offset + limit, total)} of {total}
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="min-h-11 min-w-11"
              aria-label={t`Previous page`}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              className="min-h-11 min-w-11"
              aria-label={t`Next page`}
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      <CampaignBuilderModal
        key={builderKey}
        open={showBuilder}
        onOpenChange={setShowBuilder}
        editing={editing}
        onSaved={() => {
          setOffset(0);
          refresh();
        }}
      />

      <CampaignDetailDrawer
        campaignId={openCampaignId}
        open={openCampaignId !== null}
        onOpenChange={(o) => {
          if (!o) setOpenCampaignId(null);
        }}
        onUpdated={refresh}
        onEdit={(c) => {
          setOpenCampaignId(null);
          openBuilder(c);
        }}
      />
    </div>
  );
}
